import {
  UiInvoiceStatus,
  type UiInvoice,
  type UiSubscription,
} from "@/plugins/controls/common/billing-mappers"
import { parseBusinessDateOnly } from "@/plugins/controls/common/subscription-business-date"

function normalizeDateOnly(dateText: string | null): string | null {
  const businessDate = parseBusinessDateOnly(dateText)
  if (businessDate) {
    return dateText
  }

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateText?.trim() ?? "")
  if (!match) {
    return null
  }

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  const normalizedDate = `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`

  return parseBusinessDateOnly(normalizedDate) ? normalizedDate : null
}

// Returns the next auto payment date for the subscription which is either the next invoice date or the due date of the next unpaid invoice, whichever is earlier. 
// If there are no unpaid invoices, it returns the next invoice date. If there are no valid dates, it returns null.
export function getAutopaymentDate(subscription: UiSubscription, invoices: UiInvoice[]): string | null {
  const candidateDates = [normalizeDateOnly(subscription.nextInvoiceDate)]

  for (const invoice of invoices) {
    const dueDate = normalizeDateOnly(invoice.dueDate)
    if (
      invoice.subscriptionId !== subscription.id ||
      (invoice.status !== UiInvoiceStatus.Due && invoice.status !== UiInvoiceStatus.Overdue) ||
      !dueDate
    ) {
      continue
    }

    candidateDates.push(dueDate)
  }

  return candidateDates
    .filter((date): date is string => parseBusinessDateOnly(date) !== null)
    .sort((left, right) => {
      const leftParts = parseBusinessDateOnly(left)!
      const rightParts = parseBusinessDateOnly(right)!
      return Date.UTC(leftParts.year, leftParts.month - 1, leftParts.day) - Date.UTC(rightParts.year, rightParts.month - 1, rightParts.day)
    })[0] ?? null
}